import JPush from 'jpush-react-native'
import { Platform } from 'react-native'

/**
 * 检查 JPush 是否可用
 * Web 平台不支持 JPush
 * 注意：JPush 已经在原生层（iOS AppDelegate/Android MainActivity）初始化，
 * 不需要在 JS 层调用 init()
 */
function isJPushAvailable(): boolean {
  // Web 平台不支持 JPush
  if (Platform.OS === 'web') {
    console.log('[JPush#isJPushAvailable] 平台检查: Web 平台不支持 JPush')
    return false
  }

  // 检查 JPush 模块是否存在
  if (!JPush) {
    console.log('[JPush#isJPushAvailable] 模块检查: JPush 模块不存在')
    return false
  }

  if (typeof JPush.getRegistrationID !== 'function') {
    console.log('[JPush#isJPushAvailable] 模块检查: JPush.getRegistrationID 方法不存在')
    return false
  }

  console.log('[JPush#isJPushAvailable] 模块检查: JPush 可用，平台:', Platform.OS)
  return true
}

/**
 * 获取 JPush registrationID
 * registrationID 是 JPush 分配给设备的一个唯一标识符
 */
export async function getJPushRegistrationID(): Promise<string | null> {
  console.log('[JPush#getJPushRegistrationID] 开始获取 RegistrationID...')

  // 检查 JPush 是否可用
  if (!isJPushAvailable()) {
    console.log('[JPush#getJPushRegistrationID] JPush 不可用（可能是 Web 平台或未初始化）')
    return null
  }

  try {
    console.log('[JPush#getJPushRegistrationID] 调用 JPush.getRegistrationID...')
    const registrationID = await new Promise<string | null>((resolve, reject) => {
      try {
        console.log('[JPush#getJPushRegistrationID] 执行 JPush.getRegistrationID 回调...')
        JPush.getRegistrationID((result: { registerID: string } | string) => {
          console.log('[JPush#getJPushRegistrationID] getRegistrationID 回调结果:', result)
          console.log('[JPush#getJPushRegistrationID] result 类型:', typeof result)

          // JPush 可能返回字符串或对象，需要兼容两种格式
          let regId: string | null = null
          if (typeof result === 'string') {
            regId = result
          } else if (result?.registerID) {
            regId = result.registerID
          }

          if (regId) {
            console.log('[JPush#getJPushRegistrationID] ✅ 成功获取 RegistrationID:', regId)
            resolve(regId)
          } else {
            // registrationID 为空可能表示还未注册成功
            console.log('[JPush#getJPushRegistrationID] ⚠️ RegistrationID 为空，可能还未注册成功')
            resolve(null)
          }
        })
      } catch (error) {
        console.error('[JPush#getJPushRegistrationID] getRegistrationID 调用错误:', error)
        reject(error)
      }
    })
    return registrationID
  } catch (error) {
    // 记录所有错误，但区分处理
    const errorMessage = error instanceof Error ? error.message : String(error)
    console.log('[JPush#getJPushRegistrationID] 捕获到错误:', errorMessage)

    if (errorMessage.includes('getRegisterId') || errorMessage.includes('null')) {
      // JPush 原生模块还未初始化，这是正常的，稍后重试
      console.log('[JPush#getJPushRegistrationID] 原生模块可能还未初始化，稍后重试')
      return null
    }

    if (errorMessage.includes('Cannot read property')) {
      console.log('[JPush#getJPushRegistrationID] 原生模块属性读取失败，可能还未初始化')
      return null
    }

    // 其他错误打印详细信息
    console.error('[JPush#getJPushRegistrationID] 获取 RegistrationID 失败:', error)
    return null
  }
}

/**
 * 初始化并监听 JPush registrationID
 * 通过监听连接状态和定期检查来获取 registrationID
 * @param callback 注册成功回调，参数为 registrationID
 * @returns 清理函数
 */
export function initAndListenRegistrationID(callback: (registrationID: string) => void): () => void {
  // 如果 JPush 不可用，直接返回空的清理函数
  if (!isJPushAvailable()) {
    console.log('[JPush#initAndListenRegistrationID] JPush 不可用，跳过监听 RegistrationID')
    return () => {}
  }

  let hasCalled = false
  let intervalId: ReturnType<typeof setInterval> | null = null
  let connectListener: ((result: { connectEnable: boolean }) => void) | null = null

  const tryGetRegistrationID = async () => {
    if (hasCalled) {
      console.log('[JPush#initAndListenRegistrationID] 已获取过 RegistrationID，跳过本次尝试')
      return
    }

    console.log('[JPush#initAndListenRegistrationID] 尝试获取 RegistrationID...')
    const registrationID = await getJPushRegistrationID()
    if (registrationID) {
      console.log('[JPush#initAndListenRegistrationID] ✅ RegistrationID 已获取:', registrationID)
      hasCalled = true
      if (intervalId) {
        console.log('[JPush#initAndListenRegistrationID] 停止定期检查')
        clearInterval(intervalId)
        intervalId = null
      }
      callback(registrationID)
    } else {
      console.log('[JPush#initAndListenRegistrationID] ⏳ RegistrationID 还未准备好，稍后重试')
    }
  }

  // 立即尝试获取一次
  console.log('[JPush#initAndListenRegistrationID] 立即尝试获取 RegistrationID...')
  tryGetRegistrationID()

  // 监听连接状态，当连接成功时尝试获取 registrationID
  try {
    console.log('[JPush#initAndListenRegistrationID] 添加连接状态监听器...')
    connectListener = (result: { connectEnable: boolean }) => {
      console.log('[JPush#initAndListenRegistrationID] 连接状态变化:', result)
      if (result.connectEnable && !hasCalled) {
        console.log('[JPush#initAndListenRegistrationID] 连接已建立，尝试获取 RegistrationID...')
        tryGetRegistrationID()
      }
    }
    JPush.addConnectEventListener(connectListener)
    console.log('[JPush#initAndListenRegistrationID] ✅ 连接状态监听器已添加')
  } catch (error) {
    console.error('[JPush#initAndListenRegistrationID] ❌ 添加连接监听器失败:', error)
  }

  // 定期检查 registrationID（每 2 秒检查一次，最多检查 30 次，即 60 秒）
  let checkCount = 0
  const maxChecks = 30
  console.log('[JPush#initAndListenRegistrationID] 启动定期检查，每 2 秒检查一次，最多检查', maxChecks, '次')
  intervalId = setInterval(() => {
    if (hasCalled) {
      console.log('[JPush#initAndListenRegistrationID] 已获取 RegistrationID，停止定期检查')
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
      return
    }

    if (checkCount >= maxChecks) {
      console.log('[JPush#initAndListenRegistrationID] 已达到最大检查次数，停止定期检查')
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
      return
    }

    checkCount++
    console.log('[JPush#initAndListenRegistrationID] 定期检查 #' + checkCount + '/' + maxChecks)
    tryGetRegistrationID()
  }, 2000)

  // 返回清理函数
  return () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (connectListener) {
      JPush.removeListener(connectListener)
      connectListener = null
    }
  }
}

/**
 * 通知数据接口
 */
export interface JPushNotificationData {
  /**
   * 唯一标识通知消息的 ID
   */
  messageID: string
  /**
   * 通知标题
   */
  title: string
  /**
   * 通知内容
   */
  content: string
  /**
   * badge 数量 (iOS only)
   */
  badge?: string
  /**
   * 声音 (iOS only)
   */
  ring?: string
  /**
   * 附加字段
   */
  extras: Record<string, string>
  /**
   * 通知事件类型：notificationArrived（通知到达）或 notificationOpened（通知被打开）
   */
  notificationEventType: 'notificationArrived' | 'notificationOpened'
}

/**
 * 监听 JPush 的通知
 * 包括通知到达事件（前台收到通知）和通知点击事件（用户点击通知）
 * 当应用收到通知或者点击通知，会调用 callback
 * @param callback 回调函数
 * @returns 清理函数，用于移除监听器
 */
export function addJPushNotificationListener(callback: (data: JPushNotificationData) => void): () => void {
  if (!isJPushAvailable()) {
    console.log('[JPush#addJPushNotificationListener] JPush 不可用，跳过添加通知监听器')
    return () => {}
  }

  try {
    console.log('[JPush#addJPushNotificationListener] 添加通知监听器...')

    const notificationListener = (result: JPushNotificationData) => {
      console.log('[JPush#addJPushNotificationListener] 📨 收到通知事件:', result.notificationEventType)
      console.log('[JPush#addJPushNotificationListener] 通知内容:', {
        messageID: result.messageID,
        title: result.title,
        content: result.content,
        extras: result.extras,
      })

      // 通知到达事件（前台收到通知）
      if (result.notificationEventType === 'notificationArrived') {
        console.log('[JPush#addJPushNotificationListener] 调用 callback')
        callback(result)
        // 通知点击事件（用户点击通知）
      } else if (result.notificationEventType === 'notificationOpened') {
        console.log('[JPush#addJPushNotificationListener] 调用 callback')
        callback(result)
      }
    }

    JPush.addNotificationListener(notificationListener)
    console.log('[JPush#addJPushNotificationListener] ✅ 通知监听器已添加')

    // 返回清理函数
    return () => {
      console.log('[JPush#addJPushNotificationListener] 移除通知监听器...')
      try {
        JPush.removeListener(notificationListener)
        console.log('[JPush#addJPushNotificationListener] ✅ 通知监听器已移除')
      } catch (error) {
        console.error('[JPush#addJPushNotificationListener] ❌ 移除通知监听器失败:', error)
      }
    }
  } catch (error) {
    console.error('[JPush#addJPushNotificationListener] ❌ 添加通知监听器失败:', error)
    return () => {}
  }
}
